import { useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Sidebar, SidebarContent, SidebarGroup, SidebarHeader } from '@/components/ui/sidebar';
import { useLocalStorageNumber } from '@/hooks/use-local-storage';
import { cn } from '@/lib/utils';
import { useGetNotifications } from '@/modules/notifications/hooks/use-get-notifications';

import {
  useDeleteAllNotifications,
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useUnreadNotificationsCount,
} from '../../hooks';
import { NotificationList } from './notification-list';
import { NotificationTrayHeader } from './notification-tray-header';
import { NotificationTabValue, NotificationTrayTabs } from './notification-tray-tabs';

const NOTIFICATION_TRAY_WIDTH_STORAGE_KEY = 'notification_tray_width';
const NOTIFICATION_TRAY_WIDTH_DEFAULT = 320;
const NOTIFICATION_TRAY_WIDTH_MIN = 280;
const NOTIFICATION_TRAY_WIDTH_MAX = 500;

interface NotificationTrayProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Main notification tray component.
 * Displays as a secondary panel adjacent to the main sidebar.
 * Contains header, tabs for filtering, and scrollable notification list.
 */
export function NotificationTray({ isOpen, onClose }: NotificationTrayProps) {
  const { t } = useTranslation('authenticated.notifications');
  const [activeTab, setActiveTab] = useState<NotificationTabValue>(NotificationTabValue.All);
  // Width state with localStorage persistence and automatic min/max validation
  const [width, setWidth] = useLocalStorageNumber(
    NOTIFICATION_TRAY_WIDTH_STORAGE_KEY,
    NOTIFICATION_TRAY_WIDTH_DEFAULT,
    NOTIFICATION_TRAY_WIDTH_MIN,
    NOTIFICATION_TRAY_WIDTH_MAX
  );
  const [isResizing, setIsResizing] = useState(false);
  const hasDraggedRef = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      hasDraggedRef.current = false;

      const startX = e.clientX;
      const startWidth = width;

      const handleMouseMove = (e: MouseEvent) => {
        // For right-side sidebar with handle on right edge, dragging right increases width
        const delta = e.clientX - startX;

        if (Math.abs(delta) > 3) {
          if (!hasDraggedRef.current) {
            hasDraggedRef.current = true;
            setIsResizing(true);
          }
          const newWidth = Math.min(
            Math.max(startWidth + delta, NOTIFICATION_TRAY_WIDTH_MIN),
            NOTIFICATION_TRAY_WIDTH_MAX
          );
          setWidth(newWidth);
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // If didn't drag, treat as click to close
        if (!hasDraggedRef.current) {
          onClose();
        }

        setIsResizing(false);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [width, onClose]
  );

  // Fetch notifications based on active tab
  const isReadFilter = activeTab === NotificationTabValue.Unread ? false : undefined;
  const {
    notifications,
    isLoading,
    isFetchingNextPage,
    error,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useGetNotifications({ isRead: isReadFilter, enabled: isOpen });

  const markAsReadMutation = useMarkNotificationRead();
  const markAllAsReadMutation = useMarkAllNotificationsRead();
  const deleteNotificationMutation = useDeleteNotification();
  const deleteAllNotificationsMutation = useDeleteAllNotifications();

  // Fetch unread count to determine if unread tab should be enabled
  const { count: unreadCount } = useUnreadNotificationsCount({ enabled: isOpen });
  const hasUnreadNotifications = unreadCount > 0;
  const hasNotifications = notifications.length > 0;

  const handleMarkAsRead = (notificationId: number) => {
    markAsReadMutation.mutate(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleDelete = (notificationId: number) => {
    deleteNotificationMutation.mutate(notificationId);
  };

  const handleDeleteAll = () => {
    deleteAllNotificationsMutation.mutate();
  };

  const emptyTitle =
    activeTab === NotificationTabValue.Unread
      ? t('inbox.empty.unread.title')
      : t('inbox.empty.all.title');
  const emptyDescription =
    activeTab === NotificationTabValue.Unread
      ? t('inbox.empty.unread.description')
      : t('inbox.empty.all.description');

  if (!isOpen) {
    return null;
  }

  return (
    <Sidebar
      collapsible="none"
      className="relative !h-screen border-l border-r"
      style={
        {
          width: `${width}px`,
          '--sidebar-background': 'var(--surface)',
        } as React.CSSProperties
      }
    >
      <SidebarHeader className="flex-col items-stretch gap-0 border-b p-0 shadow-sm">
        <NotificationTrayHeader
          onClose={onClose}
          onMarkAllAsRead={handleMarkAllAsRead}
          onDeleteAll={handleDeleteAll}
          isMarkingAllAsRead={markAllAsReadMutation.isPending}
          isDeletingAll={deleteAllNotificationsMutation.isPending}
          hasUnreadNotifications={hasUnreadNotifications}
          hasNotifications={hasNotifications}
        />
        <NotificationTrayTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isUnreadTabDisabled={!hasUnreadNotifications}
        />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="px-0">
          <NotificationList
            notifications={notifications}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            error={error}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            onMarkAsRead={handleMarkAsRead}
            onDelete={handleDelete}
            onRetry={refetch}
            emptyTitle={emptyTitle}
            emptyDescription={emptyDescription}
          />
        </SidebarGroup>
      </SidebarContent>
      {/* Resize handle - positioned on the right edge */}
      <div
        role="separator"
        aria-orientation="vertical"
        onMouseDown={handleMouseDown}
        className={cn(
          'absolute inset-y-0 right-0 z-20 w-1 cursor-col-resize transition-colors',
          'hover:bg-sidebar-border',
          isResizing && 'bg-sidebar-border'
        )}
      />
    </Sidebar>
  );
}
